import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// 预设模型列表
const PRESET_MODELS = [
  { id: "black-forest-labs/flux-dev", name: "Flux Dev" },
  { id: "black-forest-labs/flux-schnell", name: "Flux Schnell" },
  { id: "stability-ai/sdxl", name: "Stability AI SDXL" },
  { id: "custom", name: "自定义模型" }
];

// 常用的宽高预设
const SIZE_PRESETS = [
  { id: "custom", name: "自定义尺寸" },
  { id: "1:1-1024", name: "1:1 (1024×1024)", width: 1024, height: 1024 },
  { id: "1:1-512", name: "1:1 (512×512)", width: 512, height: 512 },
  { id: "16:9-1024", name: "16:9 (1024×576)", width: 1024, height: 576 },
  { id: "9:16-1024", name: "9:16 (576×1024)", width: 576, height: 1024 },
  { id: "4:3-1024", name: "4:3 (1024×768)", width: 1024, height: 768 },
  { id: "3:4-1024", name: "3:4 (768×1024)", width: 768, height: 1024 },
];

// 文件格式选项
const FILE_FORMATS = [
  { id: "webp", name: "WebP" },
  { id: "jpg", name: "JPG" },
  { id: "png", name: "PNG" },
];

// 响应格式选项
const RESPONSE_FORMATS = [
  { id: "b64_json", name: "Base64 JSON" },
  { id: "url", name: "URL" },
];

const formSchema = z.object({
  baseURL: z.string().url("请输入有效的URL"),
  apiKey: z.string().min(1, "API密钥是必需的"),
  modelSelect: z.string().min(1, "请选择模型"),
  customModel: z.string().optional(),
  responseFormat: z.string().default("b64_json"),
  sizePreset: z.string().default("1:1-1024"),
  width: z.coerce.number().int().min(64).max(2048),
  height: z.coerce.number().int().min(64).max(2048),
  fileFormat: z.string().default("webp"),
  numInferenceSteps: z.coerce.number().int().min(1).max(100),
  negativePrompt: z.string().optional(),
  seed: z.coerce.number().int().optional(),
  prompt: z.string().min(1, "提示词是必需的"),
});

type FormValues = z.infer<typeof formSchema>;

const defaultValues: FormValues = {
  baseURL: "https://api.studio.nebius.com/v1/",
  apiKey: "",
  modelSelect: "black-forest-labs/flux-dev",
  customModel: "",
  responseFormat: "b64_json",
  sizePreset: "1:1-1024",
  width: 1024,
  height: 1024,
  fileFormat: "webp",
  numInferenceSteps: 28,
  negativePrompt: "",
  seed: -1,
  prompt: "",
};

interface ImageGeneratorFormProps {
  onGenerate: (values: FormValues) => Promise<void>;
  isGenerating: boolean;
}

export function ImageGeneratorForm({ onGenerate, isGenerating }: ImageGeneratorFormProps) {
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [showCustomModel, setShowCustomModel] = useState(false);
  const [showCustomSize, setShowCustomSize] = useState(false);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // 监听模型和尺寸选择变化
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "modelSelect") {
        setShowCustomModel(value.modelSelect === "custom");
      }
      if (name === "sizePreset") {
        setShowCustomSize(value.sizePreset === "custom");
        
        // 当选择预设尺寸时，自动设置宽高
        if (value.sizePreset !== "custom") {
          const selectedPreset = SIZE_PRESETS.find(preset => preset.id === value.sizePreset);
          if (selectedPreset && selectedPreset.width && selectedPreset.height) {
            form.setValue("width", selectedPreset.width);
            form.setValue("height", selectedPreset.height);
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // 从localStorage加载配置
  useEffect(() => {
    const savedConfig = localStorage.getItem("imageGeneratorConfig");
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig);
        
        // 处理从旧版本升级的情况
        if (parsedConfig.model && !parsedConfig.modelSelect) {
          const isPresetModel = PRESET_MODELS.some(m => m.id === parsedConfig.model);
          if (isPresetModel) {
            parsedConfig.modelSelect = parsedConfig.model;
          } else {
            parsedConfig.modelSelect = "custom";
            parsedConfig.customModel = parsedConfig.model;
          }
        }
        
        // 检查尺寸是否匹配预设
        if (!parsedConfig.sizePreset) {
          const matchingPreset = SIZE_PRESETS.find(
            preset => preset.width === parsedConfig.width && preset.height === parsedConfig.height
          );
          parsedConfig.sizePreset = matchingPreset ? matchingPreset.id : "custom";
        }
        
        // 检查文件格式
        if (!parsedConfig.fileFormat) {
          parsedConfig.fileFormat = "webp";
        }
        
        form.reset(parsedConfig);
        
        // 设置状态
        setShowCustomModel(parsedConfig.modelSelect === "custom");
        setShowCustomSize(parsedConfig.sizePreset === "custom");
        
        toast.success("已从本地存储加载配置");
      } catch (error) {
        toast.error("加载保存的配置失败");
        console.error("配置加载错误:", error);
      }
    }
    setIsLoadingConfig(false);
  }, [form]);

  // 保存配置到localStorage并提交
  const onSubmit = async (values: FormValues) => {
    try {
      // 创建最终提交的数据，处理模型选择
      const submissionData = {
        ...values,
        model: values.modelSelect === "custom" ? values.customModel : values.modelSelect,
        extra_body: {
          response_extension: values.fileFormat,
          width: values.width,
          height: values.height,
          num_inference_steps: values.numInferenceSteps,
          negative_prompt: values.negativePrompt,
          seed: values.seed,
        }
      };
      
      localStorage.setItem("imageGeneratorConfig", JSON.stringify(values));
      await onGenerate(submissionData);
    } catch (error) {
      console.error("保存配置错误:", error);
      toast.error("保存配置失败");
    }
  };

  const handleRandomSeed = () => {
    form.setValue("seed", Math.floor(Math.random() * 1000000));
  };

  if (isLoadingConfig) {
    return <div className="flex items-center justify-center p-8">正在加载配置...</div>;
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>AI图像生成器</CardTitle>
        <CardDescription>配置参数并使用AI生成图像</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">API配置</h3>
              <FormField
                control={form.control}
                name="baseURL"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>基础URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://api.studio.nebius.com/v1/" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="apiKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>API密钥</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="输入您的API密钥" {...field} />
                    </FormControl>
                    <FormDescription>您的API密钥将保存在localStorage中。</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="modelSelect"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>模型</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="选择一个模型" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PRESET_MODELS.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            {model.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {showCustomModel && (
                <FormField
                  control={form.control}
                  name="customModel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>自定义模型</FormLabel>
                      <FormControl>
                        <Input placeholder="输入自定义模型ID" {...field} />
                      </FormControl>
                      <FormDescription>输入完整的模型标识符，例如 "organization/model-name"</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="responseFormat"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>响应格式</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="选择响应格式" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {RESPONSE_FORMATS.map((format) => (
                          <SelectItem key={format.id} value={format.id}>
                            {format.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {field.value === "b64_json" ? "返回Base64编码的图像数据" : "返回图像URL"}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator className="my-4" />

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">图像参数</h3>
              
              <FormField
                control={form.control}
                name="sizePreset"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>图像尺寸</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="选择图像尺寸" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {SIZE_PRESETS.map((preset) => (
                          <SelectItem key={preset.id} value={preset.id}>
                            {preset.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {showCustomSize && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="width"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>宽度</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder="1024" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="height"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>高度</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder="1024" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
              
              <FormField
                control={form.control}
                name="fileFormat"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>文件格式</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="选择文件格式" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {FILE_FORMATS.map((format) => (
                          <SelectItem key={format.id} value={format.id}>
                            {format.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="numInferenceSteps"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>推理步数</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="28" {...field} />
                    </FormControl>
                    <FormDescription>更高的值可能产生更好的结果，但需要更长的时间。</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="seed"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>种子</FormLabel>
                      <div className="flex gap-2">
                        <FormControl>
                          <Input type="number" placeholder="-1" {...field} />
                        </FormControl>
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={handleRandomSeed}
                          className="flex-shrink-0"
                        >
                          随机
                        </Button>
                      </div>
                      <FormDescription>使用-1表示随机种子。</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="negativePrompt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>负面提示词</FormLabel>
                      <FormControl>
                        <Input placeholder="要从图像中排除的内容" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator className="my-4" />

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">提示词</h3>
              <FormField
                control={form.control}
                name="prompt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>图像提示词</FormLabel>
                    <FormControl>
                      <Input placeholder="描述您想要生成的图像..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button type="submit" className="w-full" disabled={isGenerating}>
              {isGenerating ? "生成中..." : "生成图像"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
} 